import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, DatePicker, Form, Input, Modal, Row, Space, Table, TreeSelect } from "antd";
import { isMobile } from "react-device-detect";
import { createCustomerCareLog, updateCustomerCareLog } from "@/redux/actions/customerCareLogActions";
import { _newLine, makeTree, RenderCustomerServiceStatus } from "@/utils/helpers";
import { SaveOutlined, UserOutlined } from "@ant-design/icons";
import moment from "moment";

const { TextArea } = Input;

const CustomerCareForm = (props) => {

    const dispatch = useDispatch();

    const { currentCustomer, visible, onCancel, reloadData, editingItem } = props;

    const customerId = currentCustomer?.id;

    const filterData = useSelector(state => state.filterReducer.list);
    const customerCareCategoryActiveList = filterData?.customer_care_categories;
    const listParentTree = makeTree(customerCareCategoryActiveList || [], 0);

    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = React.useState(false);

    const handleSubmit = async () => {
        if (!customerId) return;

        setIsLoading(true);

        try {
            const values = await form.validateFields();

            const payload = {
                ...values,
                called_at: values.called_at
                    ? values.called_at.format("YYYY-MM-DD HH:mm:ss")
                    : null
            };

            if (editingItem) {
                await dispatch(updateCustomerCareLog(customerId, editingItem.id, payload));
            } else {
                await dispatch(createCustomerCareLog(customerId, payload));
            }

            reloadData && reloadData();

            onClose(); // đóng + reset form

        } catch (err) {
            // Có thể show notification nếu muốn
            console.error(err);
        } finally {
            setIsLoading(false); // luôn reset loading
        }
    };

    const onClose = () => {
        form.resetFields();
        onCancel();
    };

    useEffect(() => {
        if (visible && !editingItem) {
            form.resetFields();
        }
    }, [visible, editingItem]);


    useEffect(() => {
        if (editingItem) {
            // Nếu backend chưa trả category_ids, lấy từ categories
            const categoryIds =
                editingItem.category_ids ||
                (editingItem.categories ? editingItem.categories.map((c) => c.id) : []);

            form.setFieldsValue({
                // DatePicker của antd cần moment object
                called_at: editingItem.called_at ? moment(editingItem.called_at) : null,
                category_ids: categoryIds,
                // Chuyển <br> về xuống dòng khi edit
                customer_feedback: _newLine(editingItem.customer_feedback) || ""
            });
        } else {
            form.resetFields();
        }
    }, [editingItem, form]);

    const dataSource = (currentCustomer?.service_used || []).map(item => ({
        key: item.id,
        serviceName: item.service?.name,
        serviceCode: item.service?.code,
        amount: item.amount,
        selectedAt: item.selected_at,
        raw: item,
        status: item.status,
    }));

    const columns = [
        {
            title: 'Dịch vụ',
            dataIndex: 'serviceName',
            key: 'serviceName',
            render: (text, record) => (
                <div>
                    <b>{text}</b>
                    <div style={{ fontSize: 12, color: '#888' }}>
                        Mã: {record?.serviceCode} ·
                        Giá: {record?.amount?.toLocaleString()} đ
                    </div>

                    <div style={{ fontSize: 12, color: '#888' }}>
                        Ngày chốt:{' '}
                        {record?.selectedAt
                            ? moment(record?.selectedAt).format('DD/MM/YYYY')
                            : '--'}
                    </div>
                </div>
            ),
        },
        {
            title: 'Bác sĩ',
            key: 'doctorNames',
            width: 220,
            render: (_, record) => {
                const visits = record.raw?.exam_visit;

                if (!Array.isArray(visits) || visits.length === 0) {
                    return <span style={{ color: '#aaa' }}>--</span>;
                }

                const names = new Set();

                visits.forEach(ev => {
                    if (typeof ev?.doctor_charge_id === 'string') {
                        ev.doctor_charge_id
                            .split(',')
                            .map(n => n.trim())
                            .filter(Boolean)
                            .forEach(n => names.add(n));
                    }
                });

                return names.size
                    ? <span>{Array.from(names).join(', ')}</span>
                    : <span style={{ color: '#aaa' }}>--</span>;
            },
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            align: 'right',
            render: (status) => RenderCustomerServiceStatus(status),
        },
    ];

    return (
        <>
            <Modal
                title={editingItem ? "Cập nhật CSKH" : "Thêm CSKH"}
                open={visible}
                onCancel={onClose}
                onOk={handleSubmit}
                confirmLoading={isLoading}
                destroyOnClose
                width={isMobile ? "100%" : "50%"}
                footer={
                    <div style={{ textAlign: 'right', }}>
                        <Space>
                            <Button key="back" size='large' onClick={onClose}>
                                Huỷ
                            </Button>
                            <Button
                                loading={isLoading}
                                key="submit"
                                type="primary"
                                onClick={handleSubmit}
                                size='large'
                                icon={<SaveOutlined />}
                            >
                                Lưu
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form layout="vertical" form={form}>

                    <Row gutter={16}>
                        <Col xs={24} md={24} className='mb-3'>
                            <h6><UserOutlined /> Khách hàng:&nbsp;
                                <span className='color-primary'>
                                    {(currentCustomer?.full_name || currentCustomer?.customer_name) + ' - ' + (currentCustomer?.code || currentCustomer?.customer_code)}
                                </span>
                            </h6>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} className="mb-3">
                            <Table
                                title={() => <strong>Dịch vụ khách đang sử dụng</strong>}
                                columns={columns}
                                dataSource={dataSource}
                                pagination={false}
                                size="small"
                                scroll={{ x: 'max-content' }}
                                locale={{
                                    emptyText: (
                                        <> Khách hàng chưa sử dụng dịch vụ nào</>
                                    ),
                                }}
                            />
                        </Col>
                    </Row>

                    <Form.Item
                        name="called_at"
                        label="Ngày gọi"
                        rules={[{ required: true, message: "Chọn ngày gọi" }]}
                    >
                        <DatePicker
                            showTime
                            style={{ width: "100%" }}
                            format="YYYY-MM-DD HH:mm"
                        />
                    </Form.Item>
                    <Form.Item
                        name="category_ids"
                        label="Nội dung CSKH"
                        rules={[{ required: true, message: "Chọn ít nhất 1 danh mục" }]}
                    >
                        <TreeSelect
                            style={{ width: "100%" }}
                            dropdownStyle={{ maxHeight: 450, overflow: "auto" }}
                            treeData={listParentTree || []}
                            showSearch
                            multiple
                            allowClear
                            treeNodeFilterProp="title"
                            placeholder="Chọn một hoặc nhiều nội dung CSKH"
                            treeDefaultExpandAll
                        />
                    </Form.Item>
                    <Form.Item
                        name="customer_feedback"
                        label="Phản hồi khách"
                        rules={[{ required: true, message: "Bắt buộc nhập phản hồi khách" }]}
                    >
                        <TextArea rows={8} placeholder="Nhập phản hồi khách..." />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    )
};

export default CustomerCareForm;